import React, { useCallback, useEffect, useRef } from "react";
import { IjmSelection } from "../utils";
import { useNavigation } from "@react-navigation/native";
import {
    editIjmSalaryInfo,
    EditIjmSalaryInfo,
    editIjmSelection,
    editIjmSelectionCallback,
} from "../atoms/core";
import { useRecoilState, useSetRecoilState } from "recoil";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import type { NavigationState } from "@react-navigation/routers";
import { useCurrentProfile } from "./use-current-profile";
import { ClientProfile } from "../__generated__/graphql";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useI18n } from "./i18n-hooks";
import { useApolloErrorSnackbar, useSnackbar } from "./snackbar";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIENT_PROFILE, UPDATE_PRIVATE_CLIENT_PROFILE } from "../graphql/core";

type ScreenParams = StackScreenProps<RootStackParamList>;

type CallBackType = (selection: IjmSelection | null) => void | Promise<void>;

type CallBackRefType = {
    callback: CallBackType;
    state: NavigationState<RootStackParamList>;
};

const extractLastRouteName = (
    state: NavigationState<RootStackParamList>,
): Extract<keyof RootStackParamList, string> | undefined => {
    const routes = state.routes;
    if (routes.length === 0) {
        return undefined;
    }
    const lastRoute = routes[routes.length - 1];
    return lastRoute.name;
};
/**
 * This will bring the IJM editor on screen and set the selection ( or new editor / empty selection)
 * usage:
 * const currentSelection = // assume this exists and is of type IjmSelection or {} for empty editor
 * const ijmEditor = useIJMEditor();
 * ijmEditor({currentSelection, salaryInfo: {amound: 1000, earningType: "package"}}, async (selection) => {
 *       // handle selection null=> cancel, not null => have been saved
 *   });
 */
export const useIJMEditor = () => {
    const callbackRef = useRef<CallBackRefType>();
    const setCallback = useSetRecoilState(editIjmSelectionCallback);
    const [selection, setSelection] = useRecoilState(editIjmSelection);
    const [_ijmSalaryInfo, setIjmSalaryInfo] = useRecoilState(editIjmSalaryInfo);
    const navigation = useNavigation<ScreenParams["navigation"]>();

    const effectiveCallback = useCallback(
        async (action: "save" | "cancel") => {
            const callback = callbackRef.current?.callback;
            const state = callbackRef.current?.state;
            if (action === "save") {
                await callback?.(selection);
            } else {
                await callback?.(null);
            }
            callbackRef.current = undefined;
            if (state) {
                const lastRouteName = extractLastRouteName(state);
                if (lastRouteName) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    navigation.navigate(lastRouteName);
                    return;
                }
            }
            navigation.pop();
        },
        [navigation, selection],
    );

    useEffect(() => {
        // replace the callback with the effectiveCallback but keep the original state
        setCallback((prev) => ({ ...prev, func: effectiveCallback }));
    }, [effectiveCallback, setCallback]);

    return useCallback(
        (
            options: {
                currentSelection?: IjmSelection | null;
                salaryInfo?: EditIjmSalaryInfo | null;
            },
            onChange: CallBackType,
        ) => {
            setSelection({
                ijmGroupId: options.currentSelection?.ijmGroupId,
                ijmPayFirstDays: options.currentSelection?.ijmPayFirstDays,
                ijmPayRemainingPercents: options.currentSelection?.ijmPayRemainingPercents,
            });
            setIjmSalaryInfo(options.salaryInfo || undefined); // always reset to undefined if not provided
            callbackRef.current = { callback: onChange, state: navigation.getState() };
            navigation.navigate("IJMEditorNavScreen");
        },
        [navigation, setSelection, setIjmSalaryInfo],
    );
};

export const useProfileIjmEditor = () => {
    const currentProfile = useCurrentProfile<ClientProfile>();
    const { refreshUser } = React.useContext(AuthManagerContext);
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();

    const [
        updateClientProfile,
        { error: saveClientError, loading: clientSaving, reset: clientReset },
    ] = useMutation(UPDATE_CLIENT_PROFILE);
    useApolloErrorSnackbar(saveClientError, clientReset);

    const [
        updatePrivateClientProfile,
        { error: savePrivateClientError, loading: privateClientSaving, reset: privateClientReset },
    ] = useMutation(UPDATE_PRIVATE_CLIENT_PROFILE);

    useApolloErrorSnackbar(savePrivateClientError, privateClientReset);

    const updatefunction = currentProfile?.isPrivateClient
        ? updatePrivateClientProfile
        : updateClientProfile;

    const onSubmit = useCallback(
        async (selection: IjmSelection) => {
            if (!selection) {
                return;
            }
            try {
                await updatefunction({
                    variables: {
                        changes: {
                            client: {
                                ijmGroupId: selection.ijmGroupId,
                                ijmPayFirstDays: selection.ijmPayFirstDays,
                                ijmPayRemainingPercents: selection.ijmPayRemainingPercents,
                            },
                        },
                    },
                });
                showSnackbar({
                    text: i18n.t("ijm.save_success"),
                    mode: "success",
                });
                // refetch user to update currentProfile?.client?.ijmGroup
                // to avoid isDirty to be true and avoid goBack to work without alert panel
                await refreshUser?.();
                //navigation.goBack();
            } catch (ex) {
                // console.error(ex);
                // this is handled by saveError in useMutation
            }
        },
        [updatefunction, showSnackbar, i18n, refreshUser],
    );

    const editIjm = useIJMEditor();
    const doEditIjm = useCallback(() => {
        editIjm(
            {
                currentSelection: currentProfile?.client?.ijmGroup
                    ? {
                          ijmGroupId: currentProfile?.client?.ijmGroup.id,
                          ijmPayFirstDays: currentProfile?.client?.ijmPayFirstDays,
                          ijmPayRemainingPercents: currentProfile?.client?.ijmPayRemainingPercents,
                      }
                    : {},
                salaryInfo: undefined,
            },
            async (selection) => {
                if (selection) {
                    await onSubmit(selection);
                }
            },
        );
    }, [
        currentProfile?.client?.ijmGroup,
        currentProfile?.client?.ijmPayFirstDays,
        currentProfile?.client?.ijmPayRemainingPercents,
        editIjm,
        onSubmit,
    ]);

    return { edit: doEditIjm, saving: clientSaving || privateClientSaving };
};
